"use client";

import {
  type AppShortcutItem,
  onAppShortcut,
  scanDocumentToPdf,
  setAppShortcuts,
} from "@v1/native";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { toast } from "sonner";
import type { NativeAppKind } from "./native-app-agent";
import { setPendingShare } from "./pending-share";

/** Shortcut id → page. `scan` lands where shared files land (Ablage / Neues Anliegen). */
const SHORTCUT_PATHS: Record<
  NativeAppKind,
  Readonly<Record<string, string>>
> = {
  staff: {
    inbox: "/inbox",
    tasks: "/boards/my-tasks",
    calendar: "/calendar",
    scan: "/drive",
  },
  portal: {
    new: "/portal/tasks/new",
    scan: "/portal/tasks/new",
    documents: "/portal/documents",
  },
};

/** Home-screen icon quick actions (long press), per store app. */
export function useAppShortcuts(kind: NativeAppKind | null): void {
  const t = useTranslations("shared.appShortcuts");
  const tScan = useTranslations("shared.documentScan");
  const router = useRouter();

  useEffect(() => {
    if (!kind) return;
    const scanItem: AppShortcutItem = {
      id: "scan",
      title: tScan("dokumentScannen"),
      description: tScan("seitenWerdenPdf"),
      iosIcon: "doc.viewfinder",
    };
    const items: readonly AppShortcutItem[] =
      kind === "portal"
        ? [
            {
              id: "new",
              title: t("neuesAnliegen"),
              description: t("neuesAnliegenText"),
              iosIcon: "square.and.pencil",
            },
            scanItem,
            {
              id: "documents",
              title: t("dokumente"),
              description: t("dokumenteText"),
              iosIcon: "folder",
            },
          ]
        : [
            {
              id: "inbox",
              title: t("inbox"),
              description: t("inboxText"),
              iosIcon: "tray",
            },
            {
              id: "tasks",
              title: t("meineAufgaben"),
              description: t("meineAufgabenText"),
              iosIcon: "checklist",
            },
            {
              id: "calendar",
              title: t("kalender"),
              description: t("kalenderText"),
              iosIcon: "calendar",
            },
            scanItem,
          ];
    void setAppShortcuts(items);
  }, [kind, t, tScan]);

  useEffect(() => {
    if (!kind) return;
    const paths = SHORTCUT_PATHS[kind];
    let active = true;
    let cleanup: () => void = () => {};
    void onAppShortcut((id) => {
      const path = paths[id];
      if (!path) return;
      if (id !== "scan") {
        router.push(path);
        return;
      }
      // Machine stamp, not a display date: stays sortable and filename-safe.
      const zeit = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      void scanDocumentToPdf(tScan("dateiname", { zeit }))
        .then((file) => {
          if (!file) return;
          setPendingShare({ title: "", texts: [], files: [file] });
          router.push(path);
        })
        .catch(() => toast.error(tScan("fehlgeschlagen")));
    }).then((unsubscribe) => {
      if (active) cleanup = unsubscribe;
      else unsubscribe();
    });
    return () => {
      active = false;
      cleanup();
    };
  }, [kind, router, tScan]);
}
